import {
	TShopifySalesChannel,
	TShopifyStoreSettingsResponse,
} from 'redux/reducers/shopifyStores/types';
import { TShopifyStoreFlat } from './boxStructures';
import { ETypeOfGoods } from '../EditShopifyStoreDialog/types';

export type TLooseRecord = Record<string, unknown>;

export const asRecord = (value: unknown): TLooseRecord =>
	value && typeof value === 'object' && !Array.isArray(value) ? (value as TLooseRecord) : {};

const asString = (value: unknown): string | null =>
	value == null || value === '' ? null : String(value);
const asBool = (value: unknown): boolean | null => (value == null ? null : Boolean(value));
const asNumber = (value: unknown): number | null => {
	if (value == null || value === '') return null;
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
};
const asNumberArray = (value: unknown): number[] | null => {
	if (!Array.isArray(value)) return null;
	return value.filter((v) => Number.isFinite(Number(v))).map((v) => Number(v));
};

export const flattenStoreSettings = (
	settings: TShopifyStoreSettingsResponse | null,
	salesChannelsCatalog: TShopifySalesChannel[] = [],
): TShopifyStoreFlat | null => {
	if (!settings) return null;
	const store = settings.store ?? null;
	const general = asRecord(settings.config?.general);
	const exportSection = asRecord(general.export);
	const params = asRecord(general.params);
	const eas = asRecord(params.eas);
	const easAuth = asRecord(eas.auth);
	const easParams = asRecord(eas.params);
	const salesChannels = asRecord(params.sales_channels);
	const shippingIntegration = asRecord(params.shipping_integration);

	const storeId = asNumber(store?.store_id);
	return {
		id: storeId ?? 0,
		store_id: storeId,
		store_url: asString(store?.store_url),
		store_front_url: asString(store?.store_front_url),
		name: asString(store?.name),
		blocked_at: asString(store?.blocked_at),
		disabled_at: asString(store?.disabled_at),

		enabled: asBool(general.enabled),
		enabled_at: asString(general.enabled_at),
		discounts: asBool(general.discounts),
		export_period: asNumber(exportSection.period),
		export_enabled: asBool(exportSection.enabled),
		export_updated_at: asString(exportSection.updated_at),

		eas_is_sandbox: asBool(easAuth.is_sandbox),
		eas_id: asString(easAuth.id),
		eas_url: asString(easAuth.url),
		eas_secret: asString(easAuth.secret),
		eas_id_sandbox: asString(easAuth.id_sandbox),
		eas_url_sandbox: asString(easAuth.url_sandbox),
		eas_secret_sandbox: asString(easAuth.secret_sandbox),

		immediately_process_paid_orders: asBool(easParams.immediately_process_paid_orders),
		weight_unit: asString(easParams.weight_unit),
		act_as_disclosed_agent: asBool(easParams.act_as_disclosed_agent),
		hs6p_received: asString(easParams.hs6p_received),
		type_of_goods: easParams.type_of_goods as ETypeOfGoods,
		reduced_tbe_vat_group: asBool(easParams.reduced_tbe_vat_group),
		seller_registration_country: asString(easParams.seller_registration_country),
		originating_country: asString(easParams.originating_country),
		location_warehouse_country: asString(easParams.location_warehouse_country),
		order_tags_enable: asBool(easParams.order_tags_enable),
		handle_ireland_as_ioss: asBool(easParams.handle_ireland_as_ioss),
		swisspost_full_ddp_delivery_customs_description: asBool(
			easParams.swisspost_full_ddp_delivery_customs_description,
		),

		sales_channels_enabled: asBool(salesChannels.enabled),
		sales_channels_selected_will_be_processed: (() => {
			const ids = asNumberArray(salesChannels.sales_channels_selected_will_be_processed);
			return ids;
		})(),
		sales_channels_selected_titles: (() => {
			const ids = asNumberArray(salesChannels.sales_channels_selected_will_be_processed);
			if (!ids) return null;
			const titleByAppId = new Map<number, string>();
			salesChannelsCatalog.forEach((channel) => {
				titleByAppId.set(channel.app_id, channel.title);
			});
			return ids.map((id) => titleByAppId.get(id) ?? String(id));
		})(),
		shipping_integration_enabled: asBool(shippingIntegration.enabled),
	};
};

export const formatValue = (value: unknown): string => {
	if (value === null || value === undefined || value === '') return '—';
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (Array.isArray(value)) return value.length === 0 ? '—' : value.join(', ');
	if (typeof value === 'object') return JSON.stringify(value);
	return String(value);
};

/**
 * Masks credentials so we don't leak full secrets in the UI.
 * Keeps first and last 4 chars when long enough.
 */
export const maskSecret = (value: unknown): string => {
	if (value == null || value === '') return '—';
	const str = String(value);
	if (str.length <= 8) return '••••';
	return `${str.slice(0, 4)}••••${str.slice(-4)}`;
};
